import { formatDate } from "@/lib/format";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JobDetailDto } from "@/lib/jobs";
import { jobFiles, jobFileKeys, downloadJobFile, type JobFileDto } from "@/lib/job-files";
import { FileDropzone } from "@/components/file-dropzone";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileCategoriseDialog,
  type FileCategorisationRow,
  type FileCategory,
} from "@/components/files/FileCategoriseDialog";
import { FileThumbnail } from "@/components/files/FileThumbnail";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// JobFilesTab — Plan II Files tab (matches Job - files.png).
//
// Renders the new FileDropzone above a 3-sub-tab table (Job/Quote Files,
// Client Files, Email Images). Multi-file upload auto-opens the
// FileCategoriseDialog with the freshly-uploaded files; single uploads land
// in the table without prompting. Row-level "Categorise" action re-opens the
// dialog for one file.
//
// Backend hooks (useJobFiles / useFileCategories) referenced in the plan
// don't exist yet — this tab speaks to the existing `jobFiles.list` /
// `jobFiles.upload` REST shims directly and uses a hardcoded local category
// list as the simplest stub (called out in the runbook).
// ---------------------------------------------------------------------------

type SubTab = "job-quote" | "client" | "email-images";

// Hardcoded category stub — matches the plan's escalation note (no
// system-parameter wiring exists yet). Replace with `useFileCategories()`
// when the backend hook ships.
const DEFAULT_CATEGORIES: FileCategory[] = [
  { id: "drawing", label: "Drawing" },
  { id: "po", label: "PO" },
  { id: "spec", label: "Spec" },
  { id: "misc", label: "Misc" },
];

export interface JobFilesTabProps {
  job: Pick<JobDetailDto, "jobId">;
}

interface UploadedRow {
  jobFileId: number;
  jobFileVersionId: number;
  originalFileName: string;
  contentType: string | null;
  hasThumbnail: boolean;
}

/**
 * The thumbnail node for an existing job file. Passes a real thumbnail URL only
 * when the backend reported one for the current version; otherwise `url={null}`
 * so FileThumbnail draws a per-type icon and issues zero requests.
 */
function fileThumbnail(f: JobFileDto) {
  const url =
    f.hasThumbnail && f.currentVersionId != null
      ? jobFiles.thumbnailUrl(f.jobId, f.jobFileId, f.currentVersionId)
      : null;
  return (
    <FileThumbnail url={url} contentType={f.currentContentType} alt={f.originalFileName} />
  );
}

function formatBytes(n: number | null): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Client-side scope filter. The current backend doesn't split files by
 * scope, so all uploads land in the "Job/Quote Files" bucket and the
 * other two sub-tabs are empty placeholders. When a backend `scope` field
 * lands, swap this for the real partition.
 */
function filterByScope(files: JobFileDto[], scope: SubTab): JobFileDto[] {
  if (scope === "job-quote") return files;
  return [];
}

/**
 * Thrown by saveCategoriesMut when one or more rows in a batch PATCH failed.
 * The mutation's onError inspects this and surfaces a per-row toast for each
 * failure while the dialog stays open for the user to retry.
 */
class BatchPartialError extends Error {
  constructor(
    public readonly failures: Array<{
      row: { id: string; categoryId: string | null };
      name: string;
      reason: unknown;
    }>,
  ) {
    super(`${failures.length} row(s) failed to save`);
    this.name = "BatchPartialError";
  }
}

export function JobFilesTab({ job }: JobFilesTabProps) {
  const qc = useQueryClient();
  const [sub, setSub] = useState<SubTab>("job-quote");
  const [categoriseOpen, setCategoriseOpen] = useState(false);
  const [pending, setPending] = useState<FileCategorisationRow[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (file: JobFileDto) => {
    if (file.currentVersionId == null) return;
    setDownloadingId(file.jobFileId);
    try {
      await downloadJobFile(
        job.jobId,
        file.jobFileId,
        file.currentVersionId,
        file.originalFileName,
      );
    } catch (err) {
      toast.error(
        `Download failed: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const filesQuery = useQuery({
    queryKey: jobFileKeys.list(job.jobId),
    queryFn: () => jobFiles.list(job.jobId),
    enabled: !!job.jobId,
  });

  const uploadMut = useMutation({
    mutationFn: async (filesIn: File[]): Promise<UploadedRow[]> => {
      // Sequential to avoid hammering the upload endpoint and to keep
      // FormData isolation simple. The list page typically uploads <5
      // files at a time so the latency cost is acceptable.
      const out: UploadedRow[] = [];
      for (const f of filesIn) {
        const r = await jobFiles.upload(job.jobId, f);
        out.push({
          jobFileId: r.jobFileId,
          jobFileVersionId: r.jobFileVersionId,
          originalFileName: f.name,
          contentType: f.type || null,
          hasThumbnail: r.hasThumbnail,
        });
      }
      return out;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.list(job.jobId) });
    },
    onError: (err: Error) => toast.error(`Upload failed: ${err.message}`),
  });

  // Categorisation save: N parallel PATCH calls via Promise.allSettled.
  // Successful rows persist immediately. If any row fails, surface a
  // per-row toast and throw a BatchPartialError so the dialog stays open
  // (FileCategoriseDialog only closes when onSave resolves). Each row's
  // own rowVersionBase64 from the cached file list provides the
  // optimistic-concurrency token.
  const saveCategoriesMut = useMutation({
    mutationFn: async (rows: Array<{ id: string; categoryId: string | null }>) => {
      const currentFiles = filesQuery.data ?? [];
      const settled = await Promise.allSettled(
        rows.map((r) => {
          const fileId = Number(r.id);
          const file = currentFiles.find((f) => f.jobFileId === fileId);
          if (!file) {
            return Promise.reject(new Error(`File ${fileId} not in cache`));
          }
          return jobFiles.patchCategory(job.jobId, fileId, {
            category: r.categoryId,
            rowVersionBase64: file.rowVersionBase64,
          });
        }),
      );
      const failures = settled.flatMap((res, i) => {
        if (res.status !== "rejected") return [];
        const row = rows[i]!;
        const file = currentFiles.find((f) => f.jobFileId === Number(row.id));
        return [{
          row,
          name:   file?.originalFileName ?? row.id,
          // res.reason from Promise.allSettled is typed as `any` in the
          // standard library; narrow to unknown so the lint rule is happy
          // and downstream consumers must use an `instanceof Error` guard.
          reason: res.reason as unknown,
        }];
      });
      if (failures.length > 0) throw new BatchPartialError(failures);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: jobFileKeys.list(job.jobId) });
      toast.success("Categories saved.");
    },
    onError: (err: Error) => {
      if (err instanceof BatchPartialError) {
        // Some rows may have persisted — invalidate so the user sees what landed.
        void qc.invalidateQueries({ queryKey: jobFileKeys.list(job.jobId) });
        for (const f of err.failures) {
          const msg = f.reason instanceof Error ? f.reason.message : "unknown error";
          toast.error(`Save failed for ${f.name}: ${msg}`);
        }
      } else {
        toast.error(`Save failed: ${err.message}`);
      }
    },
  });

  const handleFiles = async (filesIn: File[]) => {
    if (!filesIn.length) return;
    const uploaded = await uploadMut.mutateAsync(filesIn);
    if (uploaded.length > 1) {
      setPending(
        uploaded.map((f) => ({
          id: String(f.jobFileId),
          name: f.originalFileName,
          // Only fresh uploads that produced a thumbnail get a node; otherwise
          // leave it undefined so the dialog shows its own placeholder.
          thumbnail: f.hasThumbnail ? (
            <FileThumbnail
              url={jobFiles.thumbnailUrl(job.jobId, f.jobFileId, f.jobFileVersionId)}
              contentType={f.contentType}
              alt={f.originalFileName}
            />
          ) : undefined,
        })),
      );
      setCategoriseOpen(true);
    }
  };

  const handleRowCategorise = (f: JobFileDto) => {
    setPending([
      { id: String(f.jobFileId), name: f.originalFileName, thumbnail: fileThumbnail(f) },
    ]);
    setCategoriseOpen(true);
  };

  const handleSaveCategories = async (
    rows: Array<{ id: string; categoryId: string | null }>,
  ) => {
    try {
      await saveCategoriesMut.mutateAsync(rows);
    } finally {
      // Clear regardless of outcome: on success the dialog closes anyway;
      // on partial failure the dialog stays open via FileCategoriseDialog's
      // own catch, and the next Categorise click will repopulate `pending`
      // from the row click handler. Without this, stale rows from a failed
      // batch could leak into a subsequent dialog open.
      setPending([]);
    }
  };

  const scopedFiles = useMemo(
    () => filterByScope(filesQuery.data ?? [], sub),
    [filesQuery.data, sub],
  );

  return (
    <div className="space-y-4">
      <FileDropzone onFiles={(f) => void handleFiles(f)} multiple />

      <Tabs value={sub} onValueChange={(v) => setSub(v as SubTab)}>
        <TabsList>
          <TabsTrigger value="job-quote">Job/Quote Files</TabsTrigger>
          <TabsTrigger value="client">Client Files</TabsTrigger>
          <TabsTrigger value="email-images">Email Images</TabsTrigger>
        </TabsList>
        <TabsContent value={sub}>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="w-20 py-2">Actions</th>
                <th className="w-12 py-2">Thumb</th>
                <th className="py-2">File</th>
                <th className="w-32 py-2">Category</th>
                <th className="w-16 py-2">Version</th>
                <th className="w-32 py-2">Uploaded</th>
                <th className="w-24 py-2">By</th>
                <th className="w-16 py-2">Size</th>
              </tr>
            </thead>
            <tbody>
              {scopedFiles.map((f) => (
                <tr key={f.jobFileId} className="border-t border-border">
                  <td className="py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRowCategorise(f)}
                    >
                      Categorise
                    </Button>
                  </td>
                  <td className="py-2">
                    {fileThumbnail(f)}
                  </td>
                  <td className="py-2">
                    {f.currentVersionId != null ? (
                      <button
                        type="button"
                        onClick={() => void handleDownload(f)}
                        disabled={downloadingId === f.jobFileId}
                        className="text-left text-primary underline-offset-2 hover:underline disabled:cursor-progress disabled:opacity-60"
                      >
                        {downloadingId === f.jobFileId
                          ? `${f.originalFileName} (downloading…)`
                          : f.originalFileName}
                      </button>
                    ) : (
                      <span>{f.originalFileName}</span>
                    )}
                  </td>
                  <td className="py-2">
                    {f.category ? (
                      <Badge variant="outline">{f.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2">
                    {f.currentVersionSeq != null ? `v${f.currentVersionSeq}` : "—"}
                  </td>
                  <td className="py-2">{f.currentUploadedAtUtc ? formatDate(f.currentUploadedAtUtc) : "—"}</td>
                  <td className="py-2">
                    {f.currentUploadedByUserName ?? "—"}
                  </td>
                  <td className="py-2">{formatBytes(f.currentSizeBytes)}</td>
                </tr>
              ))}
              {scopedFiles.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No files
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>

      <FileCategoriseDialog
        open={categoriseOpen}
        onOpenChange={setCategoriseOpen}
        files={pending}
        categories={DEFAULT_CATEGORIES}
        onSave={handleSaveCategories}
        saving={saveCategoriesMut.isPending}
      />
    </div>
  );
}
